import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { settingsAPI } from '@/lib/api';

const SettingsContext = createContext(null);

const FALLBACK = {
  company_name: 'Zynq',
  currency: 'EUR',
  date_format: 'DD/MM/YYYY',
  auto_assignment: false,
  area_unit: 'sqm',
  pipeline_stages: [
    { key: 'nieuw', label: 'Nieuw', color: '#3B82F6' },
    { key: 'gecontacteerd', label: 'Gecontacteerd', color: '#8B5CF6' },
    { key: 'gekwalificeerd', label: 'Gekwalificeerd', color: '#F59E0B' },
    { key: 'afspraak', label: 'Afspraak', color: '#10B981' },
    { key: 'onderhandeling', label: 'Onderhandeling', color: '#EC4899' },
    { key: 'gewonnen', label: 'Gewonnen', color: '#22C55E' },
    { key: 'verloren', label: 'Verloren', color: '#EF4444' },
  ],
  terminal_stages: ['gewonnen', 'verloren'],
  lead_sources: ['website', 'doorverwijzing', 'social_media', 'koude_acquisitie', 'inloop', 'e-mail_campagne', 'partner', 'webformulier', 'overig'],
  property_types: ['workshop', 'advies'],
  property_statuses: [
    { key: 'beschikbaar', label: 'Beschikbaar', color: '#10B981' },
    { key: 'gereserveerd', label: 'Gereserveerd', color: '#F59E0B' },
    { key: 'afgerond', label: 'Afgerond', color: '#3B82F6' },
    { key: 'lopend', label: 'Lopend', color: '#8B5CF6' },
  ],
  custom_fields_leads: [],
  custom_fields_properties: [],
  activity_types: [
    { key: 'bellen', label: 'Bellen' }, { key: 'email', label: 'E-mail' },
    { key: 'vergadering', label: 'Vergadering' }, { key: 'bezoek', label: 'Bezoek' },
    { key: 'bericht', label: 'Bericht' }, { key: 'notitie', label: 'Notitie' },
  ],
  follow_up_types: [
    { key: 'bellen', label: 'Bellen' }, { key: 'email', label: 'E-mail' },
    { key: 'vergadering', label: 'Vergadering' }, { key: 'bezoek', label: 'Bezoek' },
  ],
  activity_outcomes: ['positief', 'neutraal', 'negatief', 'geen_antwoord', 'terugbelverzoek'],
  property_interests: ['workshop', 'beleidsadvies', 'advies_op_maat', 'consultancy'],
  lead_timelines: ['direct', '1-3 maanden', '3-6 maanden', '6-12 maanden'],
};

const STALE_MS = 5 * 1000;

export function SettingsProvider({ children }) {
  const [settings, setSettings] = useState(FALLBACK);
  const [loaded, setLoaded] = useState(false);
  const lastFetchRef = useRef(0);

  const fetchSettings = useCallback(async (force = false) => {
    const now = Date.now();
    if (!force && lastFetchRef.current && (now - lastFetchRef.current) < STALE_MS) return;

    try {
      const res = await settingsAPI.get();
      setSettings({ ...FALLBACK, ...res.data });
      lastFetchRef.current = Date.now();
    } catch {}
    setLoaded(true);
  }, []);

  useEffect(() => { fetchSettings(true); }, [fetchSettings]);

  useEffect(() => {
    const onFocus = () => fetchSettings();
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [fetchSettings]);

  const refresh = useCallback(() => fetchSettings(true), [fetchSettings]);
  const onNavigate = useCallback(() => fetchSettings(), [fetchSettings]);

  const stageMap = {};
  (settings.pipeline_stages || []).forEach(s => { stageMap[s.key] = s; });

  const statusMap = {};
  const statuses = settings.property_statuses || [];
  if (Array.isArray(statuses)) {
    statuses.forEach(s => {
      if (typeof s === 'object' && s.key) statusMap[s.key] = s;
      else if (typeof s === 'string') statusMap[s] = { key: s, label: s.charAt(0).toUpperCase() + s.slice(1), color: '#6B7280' };
    });
  }

  const areaUnit = settings.area_unit || 'sqm';
  const areaLabel = areaUnit === 'sqm' ? 'sqm' : 'sqft';

  const formatArea = useCallback((value) => {
    if (value == null) return '\u2014';
    return `${Number(value).toLocaleString()} ${areaUnit === 'sqm' ? 'sqm' : 'sqft'}`;
  }, [areaUnit]);

  const formatCurrency = useCallback((amount) => {
    if (amount == null) return '\u2014';
    const sym = { USD: '$', EUR: '\u20AC', GBP: '\u00A3', INR: '\u20B9', AUD: 'A$', CAD: 'C$', BRL: 'R$', JPY: '\u00A5', SGD: 'S$', AED: 'AED ', CHF: 'CHF ', CNY: '\u00A5' };
    const prefix = sym[settings.currency] || settings.currency + ' ';
    return `${prefix}${Number(amount).toLocaleString()}`;
  }, [settings.currency]);

  const formatDate = useCallback((dateStr) => {
    if (!dateStr) return '\u2014';
    const d = new Date(dateStr);
    if (isNaN(d)) return dateStr;
    const fmt = settings.date_format || 'DD/MM/YYYY';
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const yyyy = d.getFullYear();
    if (fmt === 'DD/MM/YYYY') return `${dd}/${mm}/${yyyy}`;
    if (fmt === 'YYYY-MM-DD') return `${yyyy}-${mm}-${dd}`;
    return `${mm}/${dd}/${yyyy}`;
  }, [settings.date_format]);

  const formatDateTime = useCallback((dateStr) => {
    if (!dateStr) return '\u2014';
    const d = new Date(dateStr);
    if (isNaN(d)) return dateStr;
    const datePart = formatDate(dateStr);
    const time = d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    return `${datePart} ${time}`;
  }, [formatDate]);

  return (
    <SettingsContext.Provider value={{ settings, loaded, refresh, onNavigate, stageMap, statusMap, areaUnit, areaLabel, formatCurrency, formatDate, formatDateTime, formatArea }}>
      {children}
    </SettingsContext.Provider>
  );
}

export function useSettings() {
  const ctx = useContext(SettingsContext);
  if (!ctx) throw new Error('useSettings must be inside SettingsProvider');
  return ctx;
}
